import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useInput } from 'react-admin';
import { ReactComponent as ChevronDown } from 'src/assets/chevron-down.svg';
import { ReactComponent as ChevronUp } from 'src/assets/chevron-up.svg';
import { ReactComponent as CheckIcon } from 'src/assets/chevron-checkbox.svg';
import { Collapse, IconButton, InputLabel, Typography } from '@mui/material';
import { Theme } from 'src/Theme';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import TruncatedWithTooltip from '../TrancatedTypographyWithTooltip';

interface ShsCheckboxTreeProps {
    source?: string;
    label: string;
    defaultValue: number[];
    choices?: any[];
    isCountNeeded?: boolean;
}

const IndeterminateIcon = styled('span')({
    width: '10px',
    height: '10px',
    borderRadius: '2px',
    backgroundColor: Theme.palette.primary.main,
    flexShrink: 0
});

export default function ShsCheckboxTree({
    source = '10',
    label,
    defaultValue = [],
    choices = [],
    isCountNeeded = true
}: ShsCheckboxTreeProps) {
    const enabledChoices = choices.filter((choice) => !choice.disabled);
    const [checkedChoices, setCheckedChoices] = useState<number[]>([]);
    const [open, setOpen] = useState(false);
    const { field } = useInput<number[]>({ source, defaultValue });

    useEffect(() => {
        field.onChange(checkedChoices);
    }, [checkedChoices]);

    const handleChange = (event: React.ChangeEvent<any>) => {
        const { checked, value }: { checked: boolean; value: string } = event.target;

        if (checked) {
            setCheckedChoices((checkedChoices) => [...checkedChoices, +value]);
        } else {
            setCheckedChoices((checkedChoices) => checkedChoices.filter((option) => option !== +value));
        }
    };

    const handleParentCheckboxChange = () => {
        if (checkedChoices.length > 0) {
            setCheckedChoices([]);
        } else {
            setCheckedChoices(enabledChoices.map((choice) => choice.id));
        }
    };

    const averageHour = Math.floor(
        enabledChoices.reduce((acc, curr) => acc + curr.lessonCount, 0) / enabledChoices.length
    );
    const nonAverageChoice = enabledChoices.filter((choice) => choice.lessonCount !== averageHour).length;

    return (
        <Box>
            <Box display="flex" alignItems="center">
                <FormControlLabel
                    sx={{ flexGrow: 1, gap: '10px', marginLeft: 0 }}
                    label={
                        <InputLabel sx={{ display: 'flex', gap: '4px' }}>
                            <Typography
                                component="b"
                                fontSize="11px"
                                color={Theme.palette.primary.midnightBlue}
                                textTransform="lowercase">
                                {label}
                            </Typography>
                            {isCountNeeded && (
                                <Typography component="span" fontSize="11px" color={Theme.palette.primary.grayBluish}>
                                    ({averageHour || 0} год)
                                </Typography>
                            )}
                            {nonAverageChoice > 0 && (
                                <Typography component="span" fontSize="11px" color={Theme.palette.primary.grayBluish}>
                                    + {nonAverageChoice} {nonAverageChoice > 1 ? 'учні' : 'учень'}
                                </Typography>
                            )}
                        </InputLabel>
                    }
                    control={
                        <Checkbox
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '20px',
                                height: '20px',
                                borderRadius: '4px',
                                border: `1px solid ${Theme.palette.primary.main}`,
                                color: 'transparent',
                                backgroundColor:
                                    checkedChoices.length === enabledChoices.length
                                        ? Theme.palette.primary.main
                                        : 'transparent',
                                '&.Mui-disabled': {
                                    color: 'transparent',
                                    backgroundColor: '#9B9FA2',
                                    borderColor: '#9B9FA2'
                                }
                            }}
                            checked={checkedChoices.length === enabledChoices.length}
                            indeterminate={checkedChoices.length > 0 && checkedChoices.length < enabledChoices.length}
                            disabled={!averageHour}
                            disableRipple
                            checkedIcon={<CheckIcon style={{ flexShrink: 0 }} />}
                            indeterminateIcon={<IndeterminateIcon />}
                            onChange={handleParentCheckboxChange}
                        />
                    }
                />
                <IconButton sx={{ marginRight: 0 }} disableRipple onClick={() => setOpen(!open)}>
                    {open ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
                </IconButton>
            </Box>
            <Collapse in={open}>
                <Box>
                    {choices
                        .sort((a, b) => {
                            const surnameCompare = a.surname.localeCompare(b.surname);
                            if (surnameCompare !== 0) return surnameCompare;
                            return a.name.localeCompare(b.name);
                        })
                        .map((choice) => (
                            <FormControlLabel
                                key={choice.id}
                                sx={{
                                    flexGrow: 1,
                                    paddingLeft: '44px',
                                    width: '100%',
                                    '.MuiFormControlLabel-label': {
                                        width: '100%',
                                        display: 'flex'
                                    }
                                }}
                                label={
                                    <InputLabel
                                        sx={{
                                            display: 'flex',
                                            width: '100%'
                                        }}>
                                        <TruncatedWithTooltip
                                            component="span"
                                            fontSize="11px"
                                            fontWeight={600}
                                            width="100px"
                                            color={Theme.palette.primary.grayBluish}
                                            flex="1">
                                            {`${choice.surname} ${choice.name}`}
                                        </TruncatedWithTooltip>

                                        {isCountNeeded && (
                                            <Typography
                                                component="span"
                                                fontSize="11px"
                                                color={Theme.palette.primary.grayBluish}>
                                                {choice.lessonCount}
                                            </Typography>
                                        )}
                                    </InputLabel>
                                }
                                control={
                                    <Checkbox
                                        checked={checkedChoices.includes(choice.id)}
                                        onChange={handleChange}
                                        value={choice.id}
                                        disabled={choice.disabled}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '4px',
                                            border: `1px solid ${Theme.palette.primary.main}`,
                                            color: 'transparent',
                                            marginRight: '10px',
                                            '&.Mui-checked': {
                                                backgroundColor: Theme.palette.primary.main
                                            },
                                            '&.Mui-disabled': {
                                                color: 'transparent',
                                                backgroundColor: '#9B9FA2',
                                                borderColor: '#9B9FA2'
                                            }
                                        }}
                                        disableRipple
                                        checkedIcon={<CheckIcon style={{ flexShrink: 0 }} />}
                                    />
                                }
                            />
                        ))}
                </Box>
            </Collapse>
        </Box>
    );
}
